import { ReifiedPromise } from "src/helpers/ReifiedPromise";
import { exhaustiveCaseGuard, FK_nodeRef, gatherByKey_manyPerKey, maybePrettifyUSPhoneNumber, nextGlobalIntlike, NoUncheckedIndexedAccess, requireNonNull, SetEx, sortBy, sortByAccentAwareCaseInsenstive, UiOption, UiOptions, uniqueBy, unreachable, vReqT } from "src/helpers/utils";
import { computed, defineComponent, Ref, ref, watch } from "vue";
import { AssignableCodesForUsers, CoachAssignment, CoachAssignmentsPlayerAssignment, CoachAssignmentsTeamSummaryTeam, CoachAssignmentsTeamSummaryTeam_ParentInfo, CoachAssignmentUser, VolunteerAssignment, VolunteerCertificate, YouthCoachAssignment } from "./CoachAssignments.io";
import { SoccerBall } from "../SVGs";
import { teamDesignationAndMaybeName } from "../GameScheduler/calendar/GameScheduler.shared";
import { ColDef, freshSortState } from "src/modules/TableUtils";
import { dayjsOr } from "src/helpers/formatDate";
import { BasicAutoTable2, typedBasicAutoTable2Props } from "src/modules/BasicAutoTable";
import { Guid, MinDivision, MinSeason, MinTeam } from "src/interfaces/InleagueApiV1";
import { FormKit, FormKitMessages } from "@formkit/vue";
import { Btn2, btn2_redEnabledClasses } from "../UserInterface/Btn2";
import { faChevronDown, faCircleQuestion } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { coachVolunteerReportColorings } from "src/helpers/Colors";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";

export const TeamSummaryElement = defineComponent({
  props: {
    data: vReqT<ReifiedPromise<CoachAssignmentsTeamSummaryTeam[]>>(),
  },
  setup(props) {
    const hardcodedVisibilityPerParent = {
      showCoachAssignments: false,
      showVolunteerAssignments: false,
      showVolunteerIntents: false,
    } as const

    const ParentVolunteerInfo = defineComponent({
      props: {
        team: vReqT<CoachAssignmentsTeamSummaryTeam>(),
        parentInfo: vReqT<CoachAssignmentsTeamSummaryTeam_ParentInfo | null>(),
      },
      setup(props) {
        return () => {
          if (props.parentInfo === null) {
            return null
          }

          return <ul class="ml-2 text-sm list-disc list-inside">
            {hardcodedVisibilityPerParent.showCoachAssignments && props
              .parentInfo
              .coachAssignments
              .filter(v => v.team.teamID === props.team.teamID)
              .map(v => {
                return <li>
                  <span class="-ml-2">{v.title}</span>
                </li>
              })
            }
            {hardcodedVisibilityPerParent.showVolunteerAssignments && props
              .parentInfo
              .volunteerAssignments
              .filter(v => v.team.teamID === props.team.teamID)
              .map(v => {
                return <li>
                  <span class="-ml-2">{v.codeName}</span>
                </li>
              })
            }
            {hardcodedVisibilityPerParent.showVolunteerIntents && props
              .parentInfo
              .volunteerIntents
              .filter(v => !v.divID || v.divID === props.team.division.divID)
              .filter(uniqueBy(v => v.code))
              .map(v => {
                return <li>
                  <span class="-ml-2">{v.code}</span>
                </li>
              })
            }
          </ul>
        }
      }
    })

    const TeamElement = defineComponent({
      props: {
        team: vReqT<CoachAssignmentsTeamSummaryTeam>(),
      },
      setup(props) {
        const showSectionColumnLabels = ref(false)
        return () => {
          return <div class="overflow-x-auto w-full">
            <table class="w-full [&_td]:p-1 [&_td]:border [&_td]:border-gray-300" style="border-collapse:collapse;">
              <tr>
                <td colspan="4" class="bg-green-800 text-white" style="padding:0 !important;">
                  <button type="button" class="px-1 w-full text-left il-buttonlike-1" onClick={() => {showSectionColumnLabels.value = !showSectionColumnLabels.value}}>
                    {teamDesignationAndMaybeName(props.team)}
                  </button>
                </td>
              </tr>
              {showSectionColumnLabels.value
                ? <tr>
                    <td class="bg-green-600 text-white text-sm">Player Name</td>
                    <td class="bg-green-600 text-white text-sm">Age</td>
                    <td class="bg-green-600 text-white text-sm">Parent 1</td>
                    <td class="bg-green-600 text-white text-sm">Parent 2</td>
                </tr>
                : null}
              {props.team.players.length === 0
                ? <tr><td colspan="4">No assigned players.</td></tr>
                : props.team.players.map(v => {
                  return <tr>
                    <td>{v.playerFirstName} {v.playerLastName}</td>
                    <td>{v.playerAge}</td>
                    <td>
                      <div>{v.parent1.firstName} {v.parent1.lastName}</div>
                      <ParentVolunteerInfo team={props.team} parentInfo={v.parent1}/>
                    </td>
                    <td>
                      <div>{v.parent2 ? `${v.parent2.firstName} ${v.parent2.lastName}` : ""}</div>
                      <ParentVolunteerInfo team={props.team} parentInfo={v.parent2}/>
                    </td>
                  </tr>
                })}
              {showSectionColumnLabels.value
                ? <tr>
                    <td colspan="2" class="bg-green-600 text-white text-sm">Volunteer Name</td>
                    <td colspan="2" class="bg-green-600 text-white text-sm">Role</td>
                </tr>
                : null}
              {props.team.coaches.length === 0
                ? <tr><td colspan="4">No assigned volunteers.</td></tr>
                : props.team.coaches.map(v => {
                  return <tr>
                    <td colspan="2">{v.user.firstName} {v.user.lastName}</td>
                    <td colspan="2">{v.title}</td>
                  </tr>
                })}
            </table>
          </div>
        }
      }
    })

    return () => {
      return <div>
        {(() => {
          switch (props.data.status) {
            case "idle": return null
            case "error": return <div>Sorry, something went wrong loading team info.</div>
            case "pending": return <div class="p-2 flex items-center gap-2"><SoccerBall/>Loading...</div>
            case "resolved": return <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(450px, 500px)); align-items:start; grid-gap: 1em;">
              {props.data.data.length === 0
                ? <div>No teams found.</div>
                : null}
              {props.data.data.map(team => {
                return <TeamElement team={team}/>
              })}
            </div>
            default: exhaustiveCaseGuard(props.data)
          }
        })()}
      </div>
    }
  }
})

export const CurrentTeamAssignmentsListingElement = defineComponent({
  props: {
    team: vReqT<MinTeam | null>(),
    data: vReqT<ReifiedPromise<{users: CoachAssignmentUser[], toDeleteForm: VolAssignmentsToDeleteForm}>>(),
  },
  emits: {
    delete: () => true,
  },
  setup(props, ctx) {
    const headerClass = "border border-gray-400 p-1"

    type DeleteTracker = Pick<VolAssignmentsToDeleteForm, "has" | "toggle" | "canDelete">
    type PivotedRowData =
      | {type: "coachAssignment", deletionInfo: DeleteTracker, coachAssignment: CoachAssignment, user: CoachAssignmentUser}
      | {type: "volunteerAssignment", deletionInfo: DeleteTracker, volunteerAssignment: VolunteerAssignment, user: CoachAssignmentUser}
      | {type: "youthCoachAssignment", deletionInfo: DeleteTracker, youthCoachAssignment: YouthCoachAssignment, user: CoachAssignmentUser}

    const pivotedRowData = computed<PivotedRowData[]>(() => {
      const data = props.data.getOrNull()
      if (!data) {
        return []
      }

      const _targetTeamID = requireNonNull(props.team).teamID // if we have `data` (and we do if we got here), we should also have the team
      const forTeam = (v: {team: MinTeam}) => v.team.teamID === _targetTeamID;

      return data.users.flatMap(user => {
        return [
          ...user.seasonalCoachAssignments.filter(forTeam).map((ca) : PivotedRowData => ({type: "coachAssignment", deletionInfo: data.toDeleteForm, coachAssignment: ca, user})),
          ...user.seasonalVolunteerAssignments.filter(forTeam).map((va) : PivotedRowData => ({type: "volunteerAssignment", deletionInfo: data.toDeleteForm, volunteerAssignment: va, user})),
          ...user.seasonalYouthCoachAssignments.filter(forTeam).map((yca) : PivotedRowData => ({type: "youthCoachAssignment", deletionInfo: data.toDeleteForm, youthCoachAssignment: yca, user}))
        ]
      })
    })

    const colDefs : ColDef<PivotedRowData>[] = [
      {
        id: "delete",
        label: "Delete",
        headerClass,
        html: v => {
          // sort of wonky that we have to `obj.foo.func(obj)` (i.e. pass obj to obj member method) but tough to mess up here (we only have the 1 row obj in scope)
          if (!v.deletionInfo.canDelete(v)) {
            return null
          }
          return <input type="checkbox" data-test="delete-checkbox" checked={v.deletionInfo.has(v)} onInput={() => { v.deletionInfo.toggle(v) }}/>
        }
      },
      {
        id: "name",
        label: "Name",
        headerClass,
        html: v => `${v.user.firstName} ${v.user.lastName}`,
        sort: sortByAccentAwareCaseInsenstive(v => v.user.lastName, v => v.user.firstName)
      },
      {
        id: "email",
        label: "E-Mail",
        headerClass,
        html: v => v.user.email,
        sort: sortByAccentAwareCaseInsenstive(v => v.user.email),
      },
      {
        id: "phoneNumber",
        label: "Phone",
        headerClass,
        html: v => maybePrettifyUSPhoneNumber(v.user.primaryPhone),
      },
      {
        id: "riskStatus",
        label: "Risk Status",
        headerClass,
        html: v => {
          return /expired/i.test(v.user.riskStatus)
            ? `${v.user.riskStatus} as of ${dayjsOr(v.user.riskStatusExpiration)?.format("M/DD/YY") ?? "???"}`
            : `${v.user.riskStatus} through ${dayjsOr(v.user.riskStatusExpiration)?.format("M/DD/YY") ?? "???"}`
        }
      },
      {
        id: "lastLogin",
        label: "Last Login",
        headerClass,
        html: v => dayjsOr(v.user.lastLogin)?.format("M/DD/YY") ?? ""
      },
      {
        id: "certs",
        label: "Certification",
        headerClass,
        html: v => v
          .user
          .volunteerCertifications
          .filter(v => certEffectivelyValid(v))
          .filter(uniqueBy(v => v.certificationDesc))
          .map(v => <div>{v.certificationDesc}</div>),
      },
      {
        id: "safeHaven",
        label: "Safe Haven",
        headerClass,
        html: v => v
          .user
          .volunteerCertifications
          .filter(v => v.isSafeHaven && certEffectivelyValid(v))
          .filter(uniqueBy(v => v.certificationDesc))
          .map(v => <div>{v.certificationDesc}</div>)
      },
      {
        id: "players",
        label: "Child Teams",
        headerClass,
        html: v => v.user.seasonalPlayers.map(pa => <div>{teamDesignationAndMaybeName(pa.team)} - {pa.playerFirstName} {pa.playerLastName}</div>)
      },
      {
        id: "position",
        label: "Position",
        headerClass,
        headerStyle: "width: 300px;",
        html: v => v.type === "coachAssignment" ? v.coachAssignment.title
          : v.type === "volunteerAssignment" ? v.volunteerAssignment.codeName
          : v.type === "youthCoachAssignment" ? "Youth Coach"
          : exhaustiveCaseGuard(v)
      }
    ]

    const sortState = freshSortState(colDefs)
    sortState.reconfigure([{colID: "name", dir: "asc"}])

    const tableProps = computed(() => typedBasicAutoTable2Props({
      colDefs: colDefs,
      rows: pivotedRowData.value,
      paginationOptions: [10, 50, "ALL"],
      sortState: sortState,
      rowKey: v => v.user.userID,
      rowAttrs: v => {
        const assignmentID = (() => {
          switch (v.type) {
            case "coachAssignment": return v.coachAssignment.assignmentID
            case "volunteerAssignment": return v.volunteerAssignment.assignmentID
            case "youthCoachAssignment": return v.youthCoachAssignment.assignmentID
            default: exhaustiveCaseGuard(v)
          }
        })()
        return {
          "data-test": `${v.type}/assignmentID=${assignmentID}`,
          style: `background-color: ${rowBgColorForUser(v.user)}`,
        }
      },
      noData: () => props.data.status === "idle" ? (!props.team ? <div class="p-2">Select a team.</div> : null)
        : props.data.status === "pending" ? <div class="p-2 flex items-center gap-2"><SoccerBall/>Loading...</div>
        : props.data.status === "error" ? <div class="p-2">Sorry, something went wrong.</div>
        : props.data.status === "resolved" ? <div class="p-2">Nothing found.</div>
        : exhaustiveCaseGuard(props.data)
    }))

    const submitDeletions = () : void => {
      ctx.emit("delete")
    }

    const selectedDeletionCount = computed(() => {
      return props.data.getOrNull()?.toDeleteForm.selectedCount ?? 0
    })

    return () => {
      return <div>
        <ColorKey/>
        <BasicAutoTable2 {...tableProps.value}/>
        <Btn2
          class="mt-2 mb-1 px-2 py-1"
          disabled={selectedDeletionCount.value === 0}
          onClick={() => submitDeletions()}
          data-test="delete-button"
        >
          {selectedDeletionCount.value === 0
            ? "Delete Selected Assignments"
            : selectedDeletionCount.value === 1 ? `Delete 1 Selected Assignment`
            : `Delete ${selectedDeletionCount.value} Selected Assignments`}
        </Btn2>
      </div>
    }
  }
})

export type CoachAssignmentFormInfo = {
  codeInfo: AssignableCodesForUsers,
  formData: {[userID: Guid]: {teamID: Guid, code: string} | NoUncheckedIndexedAccess},
  candidates: CoachAssignmentUser[]
}

export const CoachAssignmentCandidateSearch = defineComponent({
  props: {
    season: vReqT<MinSeason>(),
    division: vReqT<MinDivision>(),
    team: vReqT<MinTeam>(),
    mode: vReqT<"list" | "search">(),
    searchText: vReqT<Ref<string>>(),
    candidateFormInfo: vReqT<ReifiedPromise<CoachAssignmentFormInfo>>(),
    requireHasChildInDivSeason: vReqT<boolean>(),
    requireDeclaredIntentToBeCoachOrAssistantCoach: vReqT<boolean>(),
  },
  emits: {
    search: () => true,
    updateCoachAndVolAssignments: () => true,
    "update:requireHasChildInDivSeason": (_: boolean) => true,
    "update:requireDeclaredIntentToBeCoachOrAssistantCoach": (_: boolean) => true,
  },
  setup(props, ctx) {
    const headerClass = "border border-gray-400 p-1"

    const tableDataRenderKey = ref(nextGlobalIntlike())
    watch(() => props.candidateFormInfo.status, () => {
      tableDataRenderKey.value = nextGlobalIntlike()
    })

    type PivotedRowData = {
      codeInfo: AssignableCodesForUsers,
      formData: {[userID: Guid]: {teamID: Guid, code: string} | NoUncheckedIndexedAccess},
      candidate: CoachAssignmentUser
    }

    /**
     * per-row data, but slightly adjusted so that each row has access to all the "definitely async-resolved" formInfo
     */
    const pivotedRowData = computed<PivotedRowData[]>(() => {
      const formInfo = props.candidateFormInfo.getOrNull()
      if (!formInfo) {
        return []
      }
      return formInfo.candidates.map(v => {
        return {codeInfo: formInfo.codeInfo, formData: formInfo.formData, candidate: v}
      })
    })

    const colDefs : ColDef<PivotedRowData>[] = [
      {
        id: "name",
        label: "Name",
        headerClass,
        html: v => `${v.candidate.firstName} ${v.candidate.lastName}`,
        sort: sortByAccentAwareCaseInsenstive(v => v.candidate.lastName, v => v.candidate.firstName)
      },
      {
        id: "email",
        label: "E-Mail",
        headerClass,
        html: v => v.candidate.email,
        sort: sortByAccentAwareCaseInsenstive(v => v.candidate.email),
      },
      {
        id: "phoneNumber",
        label: "Phone",
        headerClass,
        html: v => maybePrettifyUSPhoneNumber(v.candidate.primaryPhone),
      },
      {
        id: "riskStatus",
        label: "Risk Status",
        headerClass,
        html: v => {
          return /expired/i.test(v.candidate.riskStatus)
            ? `${v.candidate.riskStatus} as of ${dayjsOr(v.candidate.riskStatusExpiration)?.format("M/DD/YY") ?? "???"}`
            : `${v.candidate.riskStatus} through ${dayjsOr(v.candidate.riskStatusExpiration)?.format("M/DD/YY") ?? "???"}`
        }
      },
      {
        id: "lastLogin",
        label: "Last Login",
        headerClass,
        html: v => dayjsOr(v.candidate.lastLogin)?.format("M/DD/YY") ?? ""
      },
      {
        id: "certs",
        label: "Certification",
        headerClass,
        html: v => v
          .candidate
          .volunteerCertifications
          .filter(v => certEffectivelyValid(v))
          .filter(uniqueBy(v => v.certificationDesc))
          .map(v => <div>{v.certificationDesc}</div>),
      },
      {
        id: "safeHaven",
        label: "Safe Haven",
        headerClass,
        html: v => v
          .candidate
          .volunteerCertifications
          .filter(v => v.isSafeHaven && certEffectivelyValid(v))
          .filter(uniqueBy(v => v.certificationDesc))
          .map(v => <div>{v.certificationDesc}</div>)
      },
      {
        id: "players",
        label: "Child Teams",
        headerClass,
        html: v => v.candidate.seasonalPlayers.map(pa => <div>{teamDesignationAndMaybeName(pa.team)}</div>)
      },
      {
        id: "position",
        label: "Position",
        headerClass,
        headerStyle: "width: 300px;",
        html: v => <CodeOptions
          focusedTeam={props.team}
          codeInfo={v.codeInfo}
          candidate={v.candidate}
          formData={requireNonNull(v.formData[v.candidate.userID])}
        />,
      }
    ]

    const CodeOptions = defineComponent({
      props: {
        focusedTeam: vReqT<MinTeam>(),
        codeInfo: vReqT<AssignableCodesForUsers>(),
        formData: vReqT<{teamID: Guid, code: string}>(),
        candidate: vReqT<CoachAssignmentUser>(),
      },
      setup(props) {
        const codeOptions = computed<UiOptions>(() => {
          if (props.candidate.riskStatusCoachResult.forbidden) {
            return {disabled: true, options: [{label: "Invalid Risk Status", value: ""}]}
          }

          const userHasCoachCodes = new Set(props.candidate.seasonalCoachAssignments.filter(v => v.team.teamID === props.focusedTeam.teamID).map(v => canonical(v.title)))
          const userHasVolCodes = new Set(props.candidate.seasonalVolunteerAssignments.filter(v => v.team.teamID === props.focusedTeam.teamID).map(v => canonical(v.codeName)))
          const userHasYouthCoachAssignment = !!props.candidate.seasonalYouthCoachAssignments.find(v => v.team.teamID === props.focusedTeam.teamID)

          const coachCodes = (props.codeInfo.coachCodesByUserID[props.candidate.userID] ?? []).map(v => {
            return userHasCoachCodes.has(canonical(v.value))
              ? alreadyAssigned(v)
              : paranoiacShallowCopy(v)
          })

          const nonCoachOrFlexCodes = props.codeInfo.nonCoachOrFlexCodes.map(v => {
            switch (v.value) {
              case "Referee": {
                return userHasVolCodes.has(canonical(v.value))
                  ? alreadyAssigned(v)
                  : paranoiacShallowCopy(v)
              }
              case "Youth": {
                return userHasYouthCoachAssignment
                  ? alreadyAssigned(v)
                  : paranoiacShallowCopy(v)
              }
              default: exhaustiveCaseGuard(v.value)
            }
          })

          const flexCodes = props.codeInfo.flexCodes.map(v => {
            return userHasVolCodes.has(canonical(v.value))
              ? alreadyAssigned(v)
              : paranoiacShallowCopy(v)
          })

          return {
            disabled: false,
            options: [
              {label: "-- Select Position --", value: ""},
              ...coachCodes,
              ...nonCoachOrFlexCodes,
              ...flexCodes
            ]
          }

          function canonical(v: string) { return v.toUpperCase() }
          function alreadyAssigned<T>(v: UiOption<T>) : UiOption<T> { return {label: `${v.label} (assigned)`, value: v.value, attrs: {disabled: true}} }
          function paranoiacShallowCopy(v: UiOption) { return {...v} }
        })

        const {hasSomePlayerInOtherTeam, teamOptions} = (() => {
          const hasSomePlayerInOtherTeam = computed(() => {
            return !!props
              .candidate
              .seasonalPlayers
              .find(v => sameCompDivButSomeTeamOtherThanTheFocusedOne(v.team))
          })

          const teamOptions = computed<UiOption[]>(() => {
            const userRelevantOptions = (() : UiOption[] => {
              const assignmentsByTeamID = gatherByKey_manyPerKey(
                props.candidate.seasonalPlayers,
                v => v.team.teamID
              );

              return [...assignmentsByTeamID.values()]
                .sort(sortBy(vs => requireNonNull(vs[0]).team.teamLetter))
                .map((vs : CoachAssignmentsPlayerAssignment[]) => {
                  const team = requireNonNull(vs[0]).team // vs should always be of length 1 or more, and we've grouped by team, so first is good enough
                  const teamname = teamDesignationAndMaybeName(team)
                  const playerNames = vs.map(v => `${v.playerFirstName} ${v.playerLastName}`).join(",")
                  return {label: `${teamname} (${playerNames})`, value: team.teamID}
                })
            })();

            if (userRelevantOptions.find(v => v.value === props.focusedTeam.teamID)) {
              // userRelevantOptions includes the focused team
              return userRelevantOptions
            }
            else {
              // userRelevantOptions does NOT include the focused team, we need to add an extra option
              const defaultOption : UiOption = {label: teamDesignationAndMaybeName(props.focusedTeam), value: props.focusedTeam.teamID}
              return [
                defaultOption,
                ...userRelevantOptions
              ]
            }
          })

          return {hasSomePlayerInOtherTeam, teamOptions}

          function sameCompDivButSomeTeamOtherThanTheFocusedOne(team: MinTeam) {
            return team.teamID !== props.focusedTeam.teamID // is not the focused team
              && team.competition.competitionUID === props.focusedTeam.competition.competitionUID // but same comp
              && team.division.divID === props.focusedTeam.division.divID // and same div
          }
        })();

        const playerInOtherTeamTooltipMessage = computed(() => {
          if (!hasSomePlayerInOtherTeam.value) {
            return ""
          }

          const user : string = `${props.candidate.firstName} ${props.candidate.lastName}`
          const comp : string = props.focusedTeam.competition.competition
          const div : string = props.focusedTeam.division.displayName || props.focusedTeam.division.division
          const team : string = teamDesignationAndMaybeName(props.focusedTeam)
          return `${user} has players in ${comp} ${div}, but for teams other than ${team}`
        })

        return () => <div>
          <FormKit
            type="select"
            disabled={codeOptions.value.disabled}
            options={codeOptions.value.options}
            v-model={props.formData.code}
            delay={0}
            data-test="code-select"
          />
          {props.formData.code && hasSomePlayerInOtherTeam.value
            ? <div>
              <div class="mt-2 flex gap-2 items-center">
                <FormKit
                  type="select"
                  disabled={props.candidate.riskStatusCoachResult.forbidden} // we shouldn't even show this <select> in such a case, but to be doubly sure
                  options={teamOptions.value}
                  v-model={props.formData.teamID}
                  data-test="team-select"
                />
                <FontAwesomeIcon
                  icon={faCircleQuestion}
                  v-tooltip={{content: playerInOtherTeamTooltipMessage.value}}
                />
              </div>
            </div>
            : null}
        </div>
      }
    })

    const sortState = freshSortState(colDefs)
    sortState.reconfigure([{colID: "name", dir: "asc"}])

    const tableProps = computed(() => typedBasicAutoTable2Props({
      colDefs: colDefs,
      rows: pivotedRowData.value,
      paginationOptions: [10, 50, "ALL"],
      sortState: sortState,
      rowKey: v => v.candidate.userID,
      rowAttrs: v => {
        return {
          "data-test": `candidate/userID=${v.candidate.userID}`,
          style: `background-color: ${rowBgColorForUser(v.candidate)}`,
        }
      },
      noData: () => props.candidateFormInfo.status === "idle" ? null
        : props.candidateFormInfo.status === "pending" ? <div class="p-2 flex items-center gap-2"><SoccerBall/>Loading...</div>
        : props.candidateFormInfo.status === "error" ? <div class="p-2">Sorry, something went wrong.</div>
        : props.candidateFormInfo.status === "resolved" ? <div class="p-2">Nothing found.</div>
        : exhaustiveCaseGuard(props.candidateFormInfo)
    }))

    const hasAtLeast1Selection = computed<boolean>(() => {
      if (props.candidateFormInfo.status !== "resolved") {
        return false
      }
      return !!Object.values(props.candidateFormInfo.data.formData).find((v) => !!v?.code)
    })

    const submit = () => {
      ctx.emit("updateCoachAndVolAssignments")
    }

    const fkNodeRef = FK_nodeRef()

    const fkbs_sink_requireDeclaredIntentToBeCoachOrAssistantCoach = {
      get value() { return props.mode === "search" ? props.requireDeclaredIntentToBeCoachOrAssistantCoach : true },
      set value(_v) { /* no-op - handled in onInput */}
    }

    return () => {
      return <div>
        <FormKit type="form" actions={false} onSubmit={() => { ctx.emit("search") }} key={props.mode} ref={fkNodeRef}>
          {props.mode === "search"
            ? <div>
              <FormKit
                type="text"
                v-model={props.searchText.value}
                placeholder="Volunteer Name"
                validation={[["required"]]}
                validationLabel="Volunteer Name"
                data-test="candidateSearch-input"
              />
            </div>
            : null}
          <div class="my-2">
            <FormKit
              label={`Only users with declared intent to be a coach in ${props.season.seasonName}, ${props.division.displayName || props.division.division}`}
              type="checkbox"
              v-model={fkbs_sink_requireDeclaredIntentToBeCoachOrAssistantCoach.value}
              disabled={props.mode === "list"}
              onInput={(v:any) => {
                if (typeof v !== "boolean") {
                  unreachable()
                }

                if (v === props.requireDeclaredIntentToBeCoachOrAssistantCoach) {
                  return
                }

                ctx.emit("update:requireDeclaredIntentToBeCoachOrAssistantCoach", v)
              }}
            />
            <FormKit
              label={`Only users having a child in ${props.season.seasonName}, ${props.division.displayName || props.division.division}`}
              type="checkbox"
              value={props.requireHasChildInDivSeason} // @formkitbs this is "initialValue" only
              onInput={(v:any) => {
                if (typeof v !== "boolean") {
                  unreachable()
                }

                if (v === props.requireHasChildInDivSeason) {
                  return
                }

                ctx.emit("update:requireHasChildInDivSeason", v)
              }}
            />
          </div>
          {props.mode === "search"
            ? <div class="my-2">
              <Btn2 type="submit" class="px-2 py-1" data-test="candidateSearch-submit">Search</Btn2>
            </div>
            : null}
        </FormKit>
        <div class="p-1 overflow-x-auto">
          {props.candidateFormInfo.status === "idle"
            ? null
            : <div key={tableDataRenderKey.value}>
              <ColorKey/>
              <BasicAutoTable2 {...tableProps.value}/>
            </div>
          }
        </div>
        <Btn2
          class="px-2 py-1"
          disabled={!hasAtLeast1Selection.value} // disabled if there is not at least 1 selection
          onClick={() => submit()}
          data-test="candidateSearchForm-submit"
        >Assign Selected Volunteers</Btn2>
        <div style="display:none;">
          {/*silence 'sorry not all fields are not filled out'*/}
          <FormKitMessages node={fkNodeRef.value?.node}/>
        </div>
      </div>
    }
  }
})

// do we need to receive "effectively-non-valid" certs from the backend? Do we need to be aware of cert validation at all on the frontend?
const certEffectivelyValid = (cert: VolunteerCertificate) => !cert.requiresValidation || cert.validated;

export type VolAssignmentsToDeleteForm = ReturnType<typeof VolAssignmentsToDeleteForm>
export function VolAssignmentsToDeleteForm(currentUserAuthZ: AssignableCodesForUsers) {
  const coachAssignmentIDs = ref(new SetEx<Guid>)
  const volunteerAssignmentIDs = ref(new SetEx<Guid>)
  const youthCoachAssignmentIDs = ref(new SetEx<Guid>)

  const reset = () => {
    coachAssignmentIDs.value.clear()
    volunteerAssignmentIDs.value.clear()
    youthCoachAssignmentIDs.value.clear()
  }

  type TaggedAssignment =
    | {type: "coachAssignment", coachAssignment: CoachAssignment}
    | {type: "volunteerAssignment", volunteerAssignment: VolunteerAssignment}
    | {type: "youthCoachAssignment", youthCoachAssignment: YouthCoachAssignment}

  /**
   * takes into account "current user permissions" against the target assignment
   */
  const canDelete = (assignment: TaggedAssignment) => {
    switch (assignment.type) {
      case "coachAssignment": {
        // on a per target-user basis, does the current user have permission to update target user's coach assignment
        return currentUserAuthZ.coachCodesByUserID[assignment.coachAssignment.user.userID]?.find(v => v.value === assignment.coachAssignment.title)
      }
      case "volunteerAssignment": {
        // these are not on a per-target-user basis, just on a per-current-user basis
        if (assignment.volunteerAssignment.codeName === "Referee") {
          return currentUserAuthZ.nonCoachOrFlexCodes.find(v => v.value === "Referee")
        }
        else {
          return currentUserAuthZ.flexCodes.find(v => v.value === assignment.volunteerAssignment.positionID)
        }
      }
      case "youthCoachAssignment": {
        // this is not on a per-target-user basis, just on a per-current-user basis
        return !!currentUserAuthZ.nonCoachOrFlexCodes.find(v => v.value === "Youth")
      }
      default: exhaustiveCaseGuard(assignment)
    }
  }

  const has = (v: TaggedAssignment) => {
    switch (v.type) {
      case "coachAssignment": return coachAssignmentIDs.value.has(v.coachAssignment.assignmentID)
      case "volunteerAssignment": return volunteerAssignmentIDs.value.has(v.volunteerAssignment.assignmentID)
      case "youthCoachAssignment": return youthCoachAssignmentIDs.value.has(v.youthCoachAssignment.assignmentID)
      default: exhaustiveCaseGuard(v)
    }
  }

  const toggle = (v: TaggedAssignment) => {
    switch (v.type) {
      case "coachAssignment":
        coachAssignmentIDs.value.invert(v.coachAssignment.assignmentID)
        return
      case "volunteerAssignment":
        volunteerAssignmentIDs.value.invert(v.volunteerAssignment.assignmentID)
        return
      case "youthCoachAssignment":
        youthCoachAssignmentIDs.value.invert(v.youthCoachAssignment.assignmentID)
        return;
      default: exhaustiveCaseGuard(v)
    }
  }

  const selectedCount = computed(() => {
    return coachAssignmentIDs.value.size
      + volunteerAssignmentIDs.value.size
      + youthCoachAssignmentIDs.value.size
  })

  return {
    // explicit SetEx<> return types here for nominal intellisense instead of structural
    get coachAssignmentIDs() : SetEx<Guid> { return coachAssignmentIDs.value },
    get volunteerAssignmentIDs() : SetEx<Guid> { return volunteerAssignmentIDs.value },
    get youthCoachAssignmentIDs() : SetEx<Guid> { return youthCoachAssignmentIDs.value },
    has,
    toggle,
    reset,
    canDelete,
    get selectedCount() { return selectedCount.value }
  }
}

export const ConfirmDeleteTeamAssignmentsModal = defineComponent({
  props: {
    data: vReqT<VolAssignmentsToDeleteForm>(),
  },
  emits: {
    confirm: () => true,
    cancel: () => true,
  },
  setup(props, ctx) {
    return () => {
      return <div>
        <div>Delete {props.data.selectedCount} {props.data.selectedCount === 1 ? "assignment" : "assignments"}?</div>
        <div class="mt-4 flex gap-2 items-center">
          <Btn2 class="px-2 py-1" data-test="delete-button" onClick={() => ctx.emit("confirm")}>Delete {props.data.selectedCount === 1 ? "assignment" : "assignments"}</Btn2>
          <Btn2 class="px-2 py-1" enabledClasses={btn2_redEnabledClasses} onClick={() => ctx.emit("cancel")}>Cancel</Btn2>
        </div>
      </div>
    }
  }
})

function rowBgColorForUser(userish: CoachAssignmentUser) : string {
  const v = userish.coachReportRowColoringInfo
  const c = coachVolunteerReportColorings;
  return v.userHasVolunteeredAsACoachForThisDivAndHasSomeCoachCertAndHasSomeCoachAssignment ? c.hasVolunteeredAsACoachForThisDivAndHasSomeCoachCertAndHasSomeCoachAssignment
    : v.userHasVolunteeredAsACoachForThisDivAndHasSomeCoachCert ? c.hasVolunteeredAsACoachForThisDivAndHasSomeCoachCert
    : v.userHasVolunteeredAsACoachForThisDiv ? c.hasVolunteeredAsACoachForThisDiv
    : v.userHasSomeCoachCertButHasNotVolunteeredAsACoachThisSeasonDiv ? c.hasSomeCoachCertButHasNotVolunteeredAsACoachThisSeasonDiv
    : "white"
}

const ColorKey = defineComponent({
  setup() {
    return () => {
      return <Disclosure>
        {({open}: {open: boolean, close: () => void}) => {
            const colors = coachVolunteerReportColorings
            return <>
              <DisclosureButton
                class="flex items-center text-sm my-2"
              >
                <FontAwesomeIcon icon={faChevronDown} class={`${open ? '' : '-rotate-90 transform'} transition-transform`}/>
                <span class="ml-1">Color Key</span>
              </DisclosureButton>
              <DisclosurePanel class="px-4 pb-2 text-sm text-gray-800">
                <p>In order of precedence:</p>
                <div class="block text-sm rounded border p-1" style={{backgroundColor: colors.hasVolunteeredAsACoachForThisDivAndHasSomeCoachCertAndHasSomeCoachAssignment}}>
                  1. <span class="text-sm">User has volunteered as a coach for this division, and has some coach cert, and has some coach assignment</span>
                </div>
                <div class="my-1 block text-sm rounded border p-1" style={{backgroundColor: colors.hasVolunteeredAsACoachForThisDivAndHasSomeCoachCert}}>
                  2. <span class="text-sm">User has volunteered as a coach for this division, and has some coach cert</span>
                </div>
                <div class="my-1 block text-sm rounded border p-1" style={{backgroundColor: colors.hasVolunteeredAsACoachForThisDiv}}>
                  3. <span class="text-sm">User has volunteered as a coach for this division</span>
                </div>
                <div class="my-1 block text-sm rounded border p-1" style={{backgroundColor: colors.hasSomeCoachCertButHasNotVolunteeredAsACoachThisSeasonDiv}}>
                  4. <span class="text-sm">User has a coach cert, but has not volunteered for this season+division</span>
                </div>
              </DisclosurePanel>
            </>
          }
        }
      </Disclosure>
    }
  }
})
